import { slugify } from 'controls/utils';
import { useState, useEffect } from 'react';
import { EXCLUDE_PRODUCTS, softwareCategorizationOrder, TIER_DESCRIPTIONS } from 'libs/constants';

function sortRCMCategories(preferenceOrder, unorderedCategories) {
  const preferenceMap = new Map();
  preferenceOrder.forEach((value, index) => {
    preferenceMap.set(value, index);
  });

  const inPreference = unorderedCategories.filter((item) => preferenceMap.has(item));
  const notInPreference = unorderedCategories.filter((item) => !preferenceMap.has(item));

  inPreference.sort((a, b) => preferenceMap.get(a) - preferenceMap.get(b));

  notInPreference.sort();

  return [...inPreference, ...notInPreference];
}

function sortRCMProducts(preferenceOrder, unorderedProducts) {
  const preferenceMap = new Map();
  preferenceOrder.forEach((value, index) => {
    preferenceMap.set(value.toLowerCase(), index);
  });

  const inPreference = unorderedProducts.filter((item) => preferenceMap.has(item.name.toLowerCase()));
  const notInPreference = unorderedProducts.filter((item) => !preferenceMap.has(item.name.toLowerCase()));

  inPreference.sort((a, b) => preferenceMap.get(a.name.toLowerCase()) - preferenceMap.get(b.name.toLowerCase()));

  notInPreference.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  return [...inPreference, ...notInPreference];
}

export const transformProductListResponse = (data) => {
  const products = data.map((item) => (item.product));

  return products.map((p) => ({ ...p, slug: slugify(p.title) }));
};

export function organizeHardwareData(data, productGroups) {
  const categories = new Set();
  const mainProducts = [];

  if (Array.isArray(data)) {
    data.forEach((categoryGroup) => {
      const category = categoryGroup.hardwareCategory;
      categories.add(category);
      const products = categoryGroup.hardwareProducts;

      products.forEach((product) => {
        mainProducts.push({
          type: 'hardware',
          category,
          name: product.name,
          productIdList: product.productIdList,
          slug: slugify(product.name),
        });
      });
    });
  }

  const orderedHardwareCategories = productGroups?.find((g) => g.group === 'HardwareCategories')?.names;
  const orderedHardwareProducts = productGroups?.find((g) => g.group === 'HardwareProducts')?.names;

  return {
    categories: orderedHardwareCategories ? sortRCMCategories(orderedHardwareCategories, Array.from(categories)) : Array.from(categories),
    mainProducts: orderedHardwareProducts ? sortRCMProducts(orderedHardwareProducts, mainProducts) : mainProducts,
  };
}

export function softwareCategorization(data, productGroups) {
  const products = data.map((entry) => entry.product);

  // get all main products that have the new software categorization
  const allRCWSWProducts = products.filter((product) => product.RCWSWCategory);
  const distinctCategories = [
    ...new Set(
      products
        .map((product) => product.RCWSWCategory)
        .filter(Boolean)
        .flatMap((cat) => cat.split(','))
        .map((cat) => cat.trim()),
    ),
  ];

  const categories = distinctCategories;
  let mainProducts = allRCWSWProducts.map((product) => {
    return {
      type: 'software',
      category: product.RCWSWCategory,
      name: product.title,
      id: product.id,
      softwareSeries: product.SoftwareSeries,
      imageUrl: product.squareThumbnail,
      slug: slugify(product.title),
    };
  });
  const EXCLUDE_CATEGORIES = ['zenbeats', 'zenology fx'];
  mainProducts = mainProducts.filter((p) => !EXCLUDE_CATEGORIES.includes(p.category));

  const orderedSoftwareCategories = softwareCategorizationOrder;

  const softwareGroup = productGroups?.find((g) => g.group === 'SoftwareMainProducts');

  const orderedSoftwareProducts = softwareGroup?.names ?? [];

  let softwareCategories = productGroups?.length
    ? sortRCMCategories(orderedSoftwareCategories, Array.from(categories))
    : Array.from(categories);

  softwareCategories = softwareCategories.filter((p) => !EXCLUDE_CATEGORIES.includes(p.toLowerCase()));

  return {
    categories: softwareCategories,
    mainProducts: orderedSoftwareProducts?.length ? sortRCMProducts(orderedSoftwareProducts, mainProducts) : mainProducts,
  };
}

export function parseCatalog(
  productList,
  hardwareMetaData,
  productGroups,
) {
  const software = softwareCategorization(
    productList,
    productGroups,
  );

  const hardware = organizeHardwareData(
    hardwareMetaData,
    productGroups,
  );

  return {
    software,
    hardware,
  };
}

export function generateHardwareImageUrl(name) {
  const processedTitle = name
    .toLowerCase()
    .replace(/[\x20-\x2C\x2E-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]/g, '_');

  const suffix = '_tn_m.png';
  const prefix = 'rc_';
  const baseUrl = 'https://static.roland.com/apps/rcm_dataset/images/hardware/';
  const resourceName = `${prefix}${processedTitle}${suffix}`;

  return baseUrl + resourceName;
}

export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= breakpoint);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [breakpoint]);

  return isMobile;
}

export function generateSoftwareImageUrl(name, softwareSeries) {
  const processedTitle = name
    .toLowerCase()
    .replace(/[\x20-\x2C\x2E-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]/g, '_');

  const suffix = '_tn_m.jpg';
  const baseUrl = 'https://static.roland.com/apps/rcm_dataset/images/products/';

  const seriesArray = softwareSeries ? softwareSeries.split(',') : [];
  const hasAnthology = seriesArray.some((series) => series.toLowerCase().includes('anthology'));

  const prefix = hasAnthology ? 'rc_anthology_' : 'rc_';

  const resourceName = `${prefix}${processedTitle}${suffix}`;
  return baseUrl + resourceName;
}

export function generateDetailsUrl(name, softwareSeries) {
  let productName = name;

  if (productName.toLowerCase().includes('zenbeats')) {
    productName = 'zenbeats';
  }

  const processedTitle = productName
    .toLowerCase()
    .replace(/[\x20-\x2C\x2E-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]/g, '_');

  const baseUrl = 'https://www.roland.com/global/products/';

  const seriesArray = softwareSeries ? softwareSeries.split(',') : [];
  const hasAnthology = seriesArray.some((series) => series.toLowerCase().includes('anthology'));

  const prefix = hasAnthology ? 'rc_anthology_' : 'rc_';

  const resourceName = `${prefix}${processedTitle}/`;
  return baseUrl + resourceName;
}

export function generateAudioUrl(name, softwareSeries) {
  const processedTitle = name
    .toLowerCase()
    .replace(/[\x20-\x2C\x2E-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]/g, '_');

  const suffix = '_pv.mp3';
  const baseUrl = 'https://static.roland.com/apps/rcm_dataset/sounds/previews/';

  const seriesArray = softwareSeries ? softwareSeries.split(',') : [];
  const hasAnthology = seriesArray.some((series) => series.toLowerCase().includes('anthology'));

  const prefix = hasAnthology ? 'rc_anthology_' : 'rc_';

  const resourceName = `${prefix}${processedTitle}${suffix}`;
  return baseUrl + resourceName;
}

export function parseMembershipsTiers(membershipData) {
  const tierMap = {};
  const tiers = [TIER_DESCRIPTIONS.CORE, TIER_DESCRIPTIONS.PRO, TIER_DESCRIPTIONS.ULTIMATE];
  const filteredData = membershipData.filter((item) => tiers.includes(item.tierDescription)).sort((a, b) => a.tierDescription.localeCompare(b.tierDescription));

  filteredData.forEach((tier) => {
    tier.products.forEach((productId) => {
      const tierName = tier.tierDescription;

      if (!tierMap[productId]) {
        tierMap[productId] = new Set();
      }

      tierMap[productId].add(tierName);
    });
  });

  Object.keys(tierMap).forEach((key) => {
    tierMap[key] = Array.from(tierMap[key]);
  });

  return tierMap;
}

export const isSearchableProduct = (product) => {
  const { title } = product;

  if (!title) return false;

  const normalizedTitle = title
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2');

  return (
    !EXCLUDE_PRODUCTS.includes(title)
    && product.AllowsPurchase !== false
    && product?.HideInMenu !== 'true'
    && !/(\W|^)test\w*/i.test(normalizedTitle)
    && !title.toLowerCase().includes('(ios)')
    && !title.toLowerCase().includes('(android)')
    && 'ProductType' in product
    && product.ProductType !== 'Lesson Services'
  );
};
